import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { api, formatApiErrorDetail } from "../../lib/api";
import { fmtDate } from "./bookingFormat";

const TIMES = Array.from({ length: 30 }, (_, i) => `${String(7 + Math.floor(i / 2)).padStart(2, "0")}:${i % 2 ? "30" : "00"}`);
const inp = "mt-1 w-full rounded-sm border border-stone-300 bg-white px-3 py-2 text-sm";

// UK wall-clock date + time -> UTC ISO (handles GMT/BST)
export const londonToIso = (date, time) => {
  const guess = new Date(`${date}T${time}:00Z`);
  const shown = new Date(guess.toLocaleString("en-US", { timeZone: "Europe/London" }));
  const utc = new Date(guess.toLocaleString("en-US", { timeZone: "UTC" }));
  return new Date(guess.getTime() - (shown - utc)).toISOString().replace(".000Z", "Z");
};

export const MoveBookingDialog = ({ row, onClose, onDone }) => {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("18:00");
  const [busy, setBusy] = useState(false);
  const [notify, setNotify] = useState(true);
  useEffect(() => { setDate(""); setTime("18:00"); setNotify(true); }, [row]);

  const save = async () => {
    if (!date) return toast.error("Please choose the new date.");
    setBusy(true);
    try {
      await api.put(`/admin/bookings/${row.id}/time`, { start_time: londonToIso(date, time), notify_patient: notify });
      toast.success(notify ? "Booking moved - patient, clinic and consultant calendars updated." : "Booking moved quietly - clinic and consultant calendars updated.");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="move-booking-dialog" className="bg-cream-50">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Change appointment time</DialogTitle>
          <DialogDescription>{row?.patient_name} · {row?.consultant} {row?.start_time ? ` · now ${fmtDate(row.start_time)}` : ""}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-ink-muted">New date</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inp} data-testid="move-date-input" /></label>
          <label className="block"><span className="text-xs uppercase tracking-[0.16em] text-ink-muted">New time (UK)</span>
            <select value={time} onChange={(e) => setTime(e.target.value)} className={inp} data-testid="move-time-select">
              {TIMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select></label>
        </div>
        {row?.in_calendly && <p data-testid="move-calendly-note" className="text-xs text-bronze-dark">This booking was made in Calendly. Moving it takes it onto the website diary - any Calendly event still open is cancelled there.</p>}
        <label className="flex items-start gap-2 text-sm">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} data-testid="move-notify-patient" className="mt-1 accent-[#8C6643]" />
          <span>Email the patient an updated calendar invite<span className="block text-xs text-ink-muted">Untick to move it quietly - their calendar will still show the old time. Clinic and consultant calendars always update.</span></span>
        </label>
        <DialogFooter>
          <button type="button" onClick={onClose} className="btn-secondary" data-testid="move-cancel-btn">Close</button>
          <button type="button" onClick={save} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="move-confirm-btn">{busy ? "Moving…" : "Move booking"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
